
/**
 * Какой это файл и как его показать.
 *
 * Главное правило: решает РАСШИРЕНИЕ файла, а не тип, который прислал браузер
 * или сервер. В общих таблицах типов многие рабочие файлы записаны
 * «картинками»: .art (ArtCAM) — image/x-jg, .dwg — image/vnd.dwg,
 * .dxf — image/vnd.dxf, .3ds — image/x-3ds, .cdr — image/x-coreldraw,
 * .psd — image/vnd.adobe.photoshop. Раньше из-за этого они пытались
 * показаться как фото. Теперь картинка — только то, что браузер правда умеет
 * нарисовать, а остальное — карточка файла: имя, формат, размер.
 *
 * Такой же список есть на сервере (src/utils/chatFileKinds.js) и в приложении
 * (src/utils/fileKind.ts) — при правке меняйте все три.
 */
const normalize = (value) => String(value || '').trim().toLowerCase()

const getExtension = (value = '') => {
    const clean = normalize(value).split(/[?#]/)[0]
    const match = clean.match(/\.([^.\\/]+)$/)
    return match ? `.${match[1]}` : ''
}

/** Расширение без точки, большими буквами: «ART», «DXF». Пусто — нет расширения. */
export const getFileExtensionLabel = (name = '') => getExtension(name).slice(1).toUpperCase().slice(0, 8)

// Картинки, которые браузер показывает сам.
const IMAGE_EXTS = new Set(['.png', '.jpg', '.jpeg', '.jpe', '.jfif', '.pjpeg', '.gif', '.webp', '.bmp', '.avif'])
const VIDEO_EXTS = new Set(['.mp4', '.mov', '.m4v', '.webm', '.mkv', '.avi', '.3gp'])
const AUDIO_EXTS = new Set(['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.opus', '.flac'])
const SPREADSHEET_EXTS = new Set(['.xls', '.xlsx', '.xlsm', '.xlsb', '.xlt', '.xltx', '.ods', '.fods', '.csv', '.tsv'])
const TEXT_EXTS = new Set([
    '.txt', '.md', '.markdown', '.json', '.xml', '.log', '.ini', '.cfg', '.conf', '.yaml', '.yml',
    '.sql', '.env', '.properties', '.toml', '.srt',
])
const WORD_EXTS = new Set(['.doc', '.docx', '.rtf', '.odt'])
const PRESENTATION_EXTS = new Set(['.ppt', '.pptx', '.odp'])
const ARCHIVE_EXTS = new Set(['.zip', '.rar', '.7z', '.tar', '.gz'])
const ONE_C_EXTS = new Set(['.mxl', '.epf', '.erf', '.dt', '.cf', '.cfu'])

/** Чертежи, которые сервер рисует картинкой: превью в чате и просмотр с зумом. */
const DRAWING_EXTS = new Set(['.dxf', '.svg'])

// Типы, которым верим, когда у файла нет понятного расширения.
const IMAGE_MIMES = new Set([
    'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/gif', 'image/webp',
    'image/bmp', 'image/x-ms-bmp', 'image/avif',
])
const VIDEO_MIMES = new Set([
    'video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska', 'video/3gpp',
    'video/x-m4v', 'video/x-msvideo', 'video/avi',
])

const ANY_3D = 'Blender или другой программе для 3D'
const ANY_CAD = 'программе для чертежей (AutoCAD, Компас, FreeCAD)'
const PHOTO_APP = 'Lightroom или другой программе для фото'

const f = (kind, title, app) => ({ kind, title, app })

/**
 * Рабочие форматы: 3D, чертежи, станки, макеты. Их расширение всегда важнее
 * типа файла — даже если браузер назвал файл «картинкой».
 */
const WORK_FORMATS = Object.freeze({
    // 3D-модели и сцены
    '.blend': f('model3d', 'Сцена Blender', 'Blender'),
    '.blend1': f('model3d', 'Резервная копия Blender', 'Blender'),
    '.blend2': f('model3d', 'Резервная копия Blender', 'Blender'),
    '.stl': f('model3d', '3D-модель STL', ANY_3D),
    '.obj': f('model3d', '3D-модель OBJ', ANY_3D),
    '.mtl': f('model3d', 'Материалы к модели OBJ', ANY_3D),
    '.fbx': f('model3d', '3D-модель FBX', ANY_3D),
    '.glb': f('model3d', '3D-модель GLB', ANY_3D),
    '.gltf': f('model3d', '3D-модель glTF', ANY_3D),
    '.dae': f('model3d', '3D-модель Collada', ANY_3D),
    '.ply': f('model3d', '3D-модель PLY (скан)', ANY_3D),
    '.3mf': f('model3d', '3D-модель для печати 3MF', 'программе для 3D-печати'),
    '.amf': f('model3d', '3D-модель для печати AMF', 'программе для 3D-печати'),
    '.abc': f('model3d', 'Анимация Alembic', ANY_3D),
    '.usd': f('model3d', 'Сцена USD', ANY_3D),
    '.usda': f('model3d', 'Сцена USD', ANY_3D),
    '.usdc': f('model3d', 'Сцена USD', ANY_3D),
    '.usdz': f('model3d', '3D-модель USDZ', ANY_3D),
    '.x3d': f('model3d', '3D-модель X3D', ANY_3D),
    '.wrl': f('model3d', '3D-модель VRML', ANY_3D),
    '.3ds': f('model3d', '3D-модель 3DS', '3ds Max или Blender'),
    '.max': f('model3d', 'Сцена 3ds Max', '3ds Max'),
    '.ma': f('model3d', 'Сцена Maya', 'Maya'),
    '.mb': f('model3d', 'Сцена Maya', 'Maya'),
    '.c4d': f('model3d', 'Сцена Cinema 4D', 'Cinema 4D'),
    '.lwo': f('model3d', '3D-модель LightWave', ANY_3D),
    '.ztl': f('model3d', 'Модель ZBrush', 'ZBrush'),
    '.zpr': f('model3d', 'Проект ZBrush', 'ZBrush'),
    '.sbsar': f('model3d', 'Материал Substance', 'Substance'),
    '.spp': f('model3d', 'Проект Substance Painter', 'Substance Painter'),
    '.vox': f('model3d', 'Воксельная модель', ANY_3D),

    // Чертежи и детали (САПР)
    '.dwg': f('cad', 'Чертёж AutoCAD', 'AutoCAD'),
    '.dxf': f('cad', 'Чертёж DXF', ANY_CAD),
    '.dwf': f('cad', 'Чертёж DWF', 'AutoCAD'),
    '.dgn': f('cad', 'Чертёж MicroStation', 'MicroStation'),
    '.step': f('cad', 'Деталь STEP', ANY_CAD),
    '.stp': f('cad', 'Деталь STEP', ANY_CAD),
    '.iges': f('cad', 'Деталь IGES', ANY_CAD),
    '.igs': f('cad', 'Деталь IGES', ANY_CAD),
    '.sat': f('cad', 'Деталь ACIS', ANY_CAD),
    '.x_t': f('cad', 'Деталь Parasolid', ANY_CAD),
    '.x_b': f('cad', 'Деталь Parasolid', ANY_CAD),
    '.sldprt': f('cad', 'Деталь SolidWorks', 'SolidWorks'),
    '.sldasm': f('cad', 'Сборка SolidWorks', 'SolidWorks'),
    '.slddrw': f('cad', 'Чертёж SolidWorks', 'SolidWorks'),
    '.ipt': f('cad', 'Деталь Inventor', 'Inventor'),
    '.iam': f('cad', 'Сборка Inventor', 'Inventor'),
    '.idw': f('cad', 'Чертёж Inventor', 'Inventor'),
    '.cdw': f('cad', 'Чертёж Компас', 'Компас-3D'),
    '.frw': f('cad', 'Фрагмент Компас', 'Компас-3D'),
    '.m3d': f('cad', 'Деталь Компас', 'Компас-3D'),
    '.a3d': f('cad', 'Сборка Компас', 'Компас-3D'),
    '.spw': f('cad', 'Спецификация Компас', 'Компас-3D'),
    '.3dm': f('cad', 'Модель Rhino', 'Rhino'),
    '.skp': f('cad', 'Модель SketchUp', 'SketchUp'),
    '.f3d': f('cad', 'Проект Fusion 360', 'Fusion 360'),
    '.catpart': f('cad', 'Деталь CATIA', 'CATIA'),
    '.catproduct': f('cad', 'Сборка CATIA', 'CATIA'),
    '.prt': f('cad', 'Деталь САПР', ANY_CAD),
    '.ifc': f('cad', 'Модель здания IFC', 'программе для BIM (Revit, ArchiCAD)'),
    '.rvt': f('cad', 'Проект Revit', 'Revit'),
    '.rfa': f('cad', 'Семейство Revit', 'Revit'),
    '.pln': f('cad', 'Проект ArchiCAD', 'ArchiCAD'),

    // Станки, гравировка, резка
    '.art': f('cnc', 'Модель ArtCAM', 'ArtCAM'),
    '.rlf': f('cnc', 'Рельеф ArtCAM', 'ArtCAM'),
    '.3dclip': f('cnc', 'Клипарт ArtCAM', 'ArtCAM'),
    '.crv': f('cnc', 'Проект Vectric (Aspire, VCarve)', 'Aspire или VCarve'),
    '.crv3d': f('cnc', 'Проект Vectric Aspire', 'Aspire'),
    '.v3m': f('cnc', 'Модель Vectric', 'Aspire или VCarve'),
    '.nc': f('cnc', 'Программа для станка (G-код)', 'программе станка'),
    '.tap': f('cnc', 'Программа для станка (G-код)', 'программе станка'),
    '.gcode': f('cnc', 'Программа для станка (G-код)', 'программе станка'),
    '.ngc': f('cnc', 'Программа для станка (G-код)', 'программе станка'),
    '.cnc': f('cnc', 'Программа для станка (G-код)', 'программе станка'),
    '.plt': f('cnc', 'Файл для плоттера (HPGL)', 'программе плоттера или CorelDRAW'),
    '.hpgl': f('cnc', 'Файл для плоттера (HPGL)', 'программе плоттера или CorelDRAW'),
    '.lbrn': f('cnc', 'Проект LightBurn', 'LightBurn'),
    '.lbrn2': f('cnc', 'Проект LightBurn', 'LightBurn'),
    '.rld': f('cnc', 'Файл лазера RDWorks', 'RDWorks'),

    // Макеты и графика
    '.cdr': f('design', 'Макет CorelDRAW', 'CorelDRAW'),
    '.cdt': f('design', 'Шаблон CorelDRAW', 'CorelDRAW'),
    '.cmx': f('design', 'Макет CorelDRAW', 'CorelDRAW'),
    '.ai': f('design', 'Макет Illustrator', 'Illustrator или CorelDRAW'),
    '.eps': f('design', 'Векторный макет EPS', 'Illustrator или CorelDRAW'),
    '.ps': f('design', 'Файл PostScript', 'Illustrator или CorelDRAW'),
    '.psd': f('design', 'Макет Photoshop', 'Photoshop'),
    '.psb': f('design', 'Большой макет Photoshop', 'Photoshop'),
    '.indd': f('design', 'Вёрстка InDesign', 'InDesign'),
    '.xcf': f('design', 'Проект GIMP', 'GIMP'),
    '.kra': f('design', 'Рисунок Krita', 'Krita'),
    '.afdesign': f('design', 'Макет Affinity Designer', 'Affinity Designer'),
    '.afphoto': f('design', 'Проект Affinity Photo', 'Affinity Photo'),
    '.sketch': f('design', 'Макет Sketch', 'Sketch'),
    '.fig': f('design', 'Макет Figma', 'Figma'),
    '.svg': f('design', 'Векторная картинка SVG', 'браузере или программе для графики'),
    '.svgz': f('design', 'Векторная картинка SVG', 'браузере или программе для графики'),
    '.tif': f('design', 'Изображение TIFF', 'программе для просмотра изображений'),
    '.tiff': f('design', 'Изображение TIFF', 'программе для просмотра изображений'),
    '.tga': f('design', 'Изображение TGA', 'программе для графики'),
    '.dds': f('design', 'Текстура DDS', 'программе для графики'),
    '.exr': f('design', 'HDR-изображение EXR', 'Blender или Photoshop'),
    '.hdr': f('design', 'HDR-изображение', 'Blender или Photoshop'),
    '.ico': f('design', 'Значок ICO', 'программе для графики'),
    '.icns': f('design', 'Значок macOS', 'программе для графики'),
    '.jp2': f('design', 'Изображение JPEG 2000', 'программе для просмотра изображений'),
    '.jxl': f('design', 'Изображение JPEG XL', 'программе для просмотра изображений'),
    '.heic': f('design', 'Фото HEIC (iPhone)', 'программе для просмотра фото'),
    '.heif': f('design', 'Фото HEIF', 'программе для просмотра фото'),
    '.dng': f('design', 'RAW-снимок', PHOTO_APP),
    '.cr2': f('design', 'RAW-снимок Canon', PHOTO_APP),
    '.cr3': f('design', 'RAW-снимок Canon', PHOTO_APP),
    '.nef': f('design', 'RAW-снимок Nikon', PHOTO_APP),
    '.arw': f('design', 'RAW-снимок Sony', PHOTO_APP),
    '.orf': f('design', 'RAW-снимок Olympus', PHOTO_APP),
    '.rw2': f('design', 'RAW-снимок Panasonic', PHOTO_APP),
    '.raf': f('design', 'RAW-снимок Fujifilm', PHOTO_APP),
    '.srw': f('design', 'RAW-снимок Samsung', PHOTO_APP),
    '.pef': f('design', 'RAW-снимок Pentax', PHOTO_APP),
})

/**
 * Документы в чате можно выбрать любые — раньше окно выбора показывало только
 * офисные форматы, и ArtCAM, AutoCAD, Blender приходилось искать через
 * «Все файлы». Список оставлен для других мест, где он нужен.
 */
export const CHAT_DOCUMENT_EXTENSIONS = [
    '.pdf',
    ...WORD_EXTS, ...SPREADSHEET_EXTS, ...TEXT_EXTS, ...PRESENTATION_EXTS,
    ...ONE_C_EXTS, ...ARCHIVE_EXTS,
    ...Object.keys(WORK_FORMATS),
]

// Пусто — в окне выбора видны все файлы.
export const CHAT_DOCUMENT_ACCEPT = ''

const MIME_BY_EXT = Object.freeze({
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.jpe': 'image/jpeg',
    '.jfif': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.gif': 'image/gif',
    '.bmp': 'image/bmp',
    '.svg': 'image/svg+xml',
    '.avif': 'image/avif',

    '.mp4': 'video/mp4',
    '.mov': 'video/quicktime',
    '.m4v': 'video/x-m4v',
    '.webm': 'video/webm',
    '.mkv': 'video/x-matroska',
    '.avi': 'video/x-msvideo',
    '.3gp': 'video/3gpp',

    '.mp3': 'audio/mpeg',
    '.wav': 'audio/wav',
    '.m4a': 'audio/mp4',
    '.aac': 'audio/aac',
    '.ogg': 'audio/ogg',
    '.oga': 'audio/ogg',
    '.opus': 'audio/ogg',
    '.flac': 'audio/flac',

    '.pdf': 'application/pdf',
    '.txt': 'text/plain',
    '.md': 'text/markdown',
    '.markdown': 'text/markdown',
    '.json': 'application/json',
    '.xml': 'application/xml',
    '.log': 'text/plain',
    '.ini': 'text/plain',
    '.cfg': 'text/plain',
    '.conf': 'text/plain',
    '.yaml': 'application/yaml',
    '.yml': 'application/yaml',
    '.sql': 'text/plain',

    '.csv': 'text/csv',
    '.tsv': 'text/tab-separated-values',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
    '.xlsb': 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
    '.ods': 'application/vnd.oasis.opendocument.spreadsheet',

    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.rtf': 'application/rtf',
    '.odt': 'application/vnd.oasis.opendocument.text',

    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.odp': 'application/vnd.oasis.opendocument.presentation',

    '.zip': 'application/zip',
    '.rar': 'application/vnd.rar',
    '.7z': 'application/x-7z-compressed',

    // У MXL/EPF/ERF/DT нет универсального web MIME, поэтому содержимое
    // остаётся бинарным, а вид файла определяется по исходному имени.
    '.mxl': 'application/octet-stream',
    '.epf': 'application/octet-stream',
    '.erf': 'application/octet-stream',
    '.dt': 'application/octet-stream',
})

/**
 * Настоящий тип файла. Безликий application/octet-stream уточняем по
 * расширению. Рабочие форматы, которые браузер выдал за картинку, видео или
 * звук (.art — image/x-jg, .dwg — image/vnd.dwg), тоже исправляем.
 */
export const inferMediaMimeType = ({ mimeType = '', name = '', source = '' } = {}) => {
    const rawMime = normalize(mimeType).split(';')[0]
    const ext = getExtension(name) || getExtension(source)

    if (WORK_FORMATS[ext] && /^(image|video|audio)\//.test(rawMime) && rawMime !== 'image/svg+xml') {
        return MIME_BY_EXT[ext] || 'application/octet-stream'
    }

    if (rawMime && rawMime !== 'application/octet-stream' && rawMime !== 'binary/octet-stream') {
        return rawMime
    }

    return MIME_BY_EXT[ext] || rawMime || 'application/octet-stream'
}

/**
 * Вид файла для просмотрщика:
 *   image, video, audio, pdf, spreadsheet, text — показываются прямо здесь;
 *   drawing — чертёж DXF/SVG: сервер рисует картинку, смотрим с зумом;
 *   word, presentation, one-c, archive — карточка файла с подсказкой;
 *   model3d, cad, cnc, design — рабочие форматы (3D, чертежи, станки, макеты);
 *   model — 3D-модель товара (отдельный просмотрщик);
 *   file — всё незнакомое.
 */
export const detectMediaDocumentKind = ({ mimeType = '', name = '', source = '', mediaType = '' } = {}) => {
    const explicit = normalize(mediaType)
    if (explicit === 'model') return 'model'

    const ext = getExtension(name) || getExtension(source)
    const mime = inferMediaMimeType({ mimeType, name, source })

    // 1. Чертежи и рабочие форматы узнаём по расширению — что бы ни сказал тип.
    if (DRAWING_EXTS.has(ext)) return 'drawing'
    if (WORK_FORMATS[ext]) return WORK_FORMATS[ext].kind
    if (ONE_C_EXTS.has(ext)) return 'one-c'

    // 2. Фото, видео, звук и PDF — по расширению.
    if (IMAGE_EXTS.has(ext)) return 'image'
    // .webm бывает и голосовым, и видео — решает тип или явный вид.
    if (VIDEO_EXTS.has(ext)) {
        return explicit === 'voice' || explicit === 'audio' || (ext === '.webm' && normalize(mimeType).startsWith('audio/'))
            ? 'audio'
            : 'video'
    }
    if (AUDIO_EXTS.has(ext)) return 'audio'
    if (ext === '.pdf' || mime === 'application/pdf') return 'pdf'

    if (
        SPREADSHEET_EXTS.has(ext) ||
        mime.includes('spreadsheet') ||
        mime.includes('ms-excel') ||
        mime.includes('opendocument.spreadsheet') ||
        mime.includes('sheet.macroenabled') ||
        mime.includes('sheet.binary.macroenabled') ||
        mime === 'text/csv' ||
        mime === 'text/tab-separated-values'
    ) return 'spreadsheet'

    if (
        TEXT_EXTS.has(ext) ||
        mime.startsWith('text/') ||
        mime === 'application/json' ||
        mime === 'application/xml' ||
        mime === 'application/yaml' ||
        mime === 'text/xml'
    ) return 'text'

    if (
        WORD_EXTS.has(ext) ||
        mime.includes('wordprocessingml') ||
        mime === 'application/msword' ||
        mime.includes('opendocument.text') ||
        mime === 'application/rtf'
    ) return 'word'

    if (
        PRESENTATION_EXTS.has(ext) ||
        mime.includes('presentationml') ||
        mime.includes('ms-powerpoint') ||
        mime.includes('opendocument.presentation')
    ) return 'presentation'

    if (ARCHIVE_EXTS.has(ext) || mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return 'archive'

    // 3. Расширения нет или оно незнакомое («IMG_0001», «photo.jpg_large»):
    // верим явному виду и только настоящим фото- и видеотипам.
    if (explicit === 'image' && (!mime || IMAGE_MIMES.has(mime) || mime === 'application/octet-stream') && !ext) return 'image'
    if (IMAGE_MIMES.has(mime)) return 'image'
    if (explicit === 'video' || VIDEO_MIMES.has(mime)) return 'video'
    if (explicit === 'voice' || explicit === 'audio' || mime.startsWith('audio/')) return 'audio'

    return 'file'
}

export const getViewerActionLabel = ({ mimeType = '', name = '', source = '', mediaType = '' } = {}) => {
    const kind = detectMediaDocumentKind({ mimeType, name, source, mediaType })

    switch (kind) {
        case 'video': return 'Запустить'
        case 'audio': return 'Прослушать'
        case 'spreadsheet': return 'Открыть таблицу'
        case 'pdf': return 'Открыть PDF'
        case 'text': return 'Открыть'
        case 'image': return 'Открыть'
        case 'drawing': return 'Смотреть чертёж'
        case 'one-c': return 'Подробнее'
        case 'word':
        case 'presentation': return 'Открыть файл'
        default: return 'Открыть'
    }
}

export const isSpreadsheetFile = (value = {}) => detectMediaDocumentKind(value) === 'spreadsheet'
export const isTextDocumentFile = (value = {}) => detectMediaDocumentKind(value) === 'text'

/** Фото, которое браузер покажет сам (в ленте, альбоме, галерее). */
export const isPhotoFile = ({ name = '', mimeType = '', mediaType = '' } = {}) => (
    detectMediaDocumentKind({ name, mimeType, mediaType }) === 'image'
)

/** Чертёж, который сервер рисует картинкой (DXF, SVG). */
export const isDrawingFile = (name = '') => DRAWING_EXTS.has(getExtension(name))

export const getDocumentKindLabel = (kind) => {
    switch (kind) {
        case 'image': return 'Изображение'
        case 'video': return 'Видео'
        case 'audio': return 'Аудио'
        case 'pdf': return 'PDF'
        case 'spreadsheet': return 'Таблица'
        case 'text': return 'Текстовый файл'
        case 'word': return 'Документ Word'
        case 'presentation': return 'Презентация'
        case 'one-c': return 'Файл 1С'
        case 'archive': return 'Архив'
        case 'model': return '3D-модель'
        case 'model3d': return '3D-модель'
        case 'cad': return 'Чертёж'
        case 'cnc': return 'Файл для станка'
        case 'design': return 'Макет'
        case 'drawing': return 'Чертёж'
        default: return 'Файл'
    }
}

/**
 * Подпись под именем файла в чате: «Модель ArtCAM», «Чертёж DXF», «PDF»;
 * для незнакомого формата — просто расширение («XYZ»), без «Фото».
 */
export const getFileTypeLabel = ({ name = '', mimeType = '' } = {}) => {
    const ext = getExtension(name)
    if (WORK_FORMATS[ext]) return WORK_FORMATS[ext].title

    const kind = detectMediaDocumentKind({ name, mimeType })
    if (kind === 'file') return getFileExtensionLabel(name)
    if (kind === 'one-c') return 'Файл 1С'
    return getDocumentKindLabel(kind)
}

/** Пояснение для просмотрщика, если сам файл показать нельзя. */
export const describeFileFormat = ({ name = '', mimeType = '' } = {}) => {
    const ext = getExtension(name)
    const work = WORK_FORMATS[ext]

    if (work) {
        return {
            title: work.title,
            hint: `Открывается в ${work.app} — обычно на компьютере. Скачайте оригинал кнопкой сверху.`,
        }
    }

    const kind = detectMediaDocumentKind({ name, mimeType })
    if (kind === 'one-c') {
        return {
            title: 'Файл 1С',
            hint: 'Формат 1С MXL/EPF/ERF пока не рендерится браузером напрямую. Файл можно скачать; для встроенного просмотра позже подключим конвертацию 1С → XLSX/PDF к этому же просмотрщику.',
        }
    }
    if (kind === 'word' || kind === 'presentation') {
        return {
            title: getDocumentKindLabel(kind),
            hint: 'Файл сохранён без изменений. Для точного отображения Word/PowerPoint потребуется серверная конвертация в PDF/HTML; скачивание оригинала доступно сверху.',
        }
    }

    const label = getFileExtensionLabel(name)
    return {
        title: label ? `Файл ${label}` : getDocumentKindLabel(kind),
        hint: 'Для этого формата встроенный просмотр пока недоступен. Оригинал можно скачать.',
    }
}

// ---------- группы документов (фильтры во вкладке «Документы») ----------

/** Фильтры «Документов» — те же группы, что на сервере (docKind). */
export const DOC_GROUPS = [
    { key: 'all', label: 'Все' },
    { key: 'office', label: 'Офис' },
    { key: 'model3d', label: '3D' },
    { key: 'cad', label: 'Чертежи' },
    { key: 'cnc', label: 'ЧПУ' },
    { key: 'design', label: 'Макеты' },
    { key: 'other', label: 'Другое' },
]

/** Группа документа: office | model3d | cad | cnc | design | other. */
export const getDocGroup = (name = '') => {
    const ext = getExtension(name)
    if (WORK_FORMATS[ext]) return WORK_FORMATS[ext].kind
    if (
        ext === '.pdf' || WORD_EXTS.has(ext) || SPREADSHEET_EXTS.has(ext) || TEXT_EXTS.has(ext) ||
        PRESENTATION_EXTS.has(ext) || ONE_C_EXTS.has(ext)
    ) return 'office'
    return 'other'
}
